"use client";

import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { AlertCircle, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Package } from "@/domain/entities/package.entity";
import { AsyncStatus, ASYNC_STATUS } from "@/hooks/use-booking-flow";
import { cn } from "@/lib/utils";
import { formatMYR } from "@/shared/utils/currency";

interface PackageStepProps {
    packages: Package[];
    isLoading: boolean;
    status: AsyncStatus;
    selectedPackage: Package | null;
    onFetchPackages: () => void | Promise<void>;
    onSelect: (pkg: Package) => void;
}

export const PackageStep: React.FC<PackageStepProps> = ({
    packages,
    isLoading,
    status,
    selectedPackage,
    onFetchPackages,
    onSelect
}) => {
    useEffect(() => {
        if (status !== ASYNC_STATUS.INITIAL) {
            return;
        }

        void onFetchPackages();
    }, [onFetchPackages, status]);

    if (status === ASYNC_STATUS.INITIAL || isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-32 gap-6">
                <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                <p className="text-muted-foreground text-xs uppercase tracking-[0.2em] font-medium animate-pulse">
                    Curating packages
                </p>
            </div>
        );
    }

    if (status === ASYNC_STATUS.FAILURE) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="flex min-h-[320px] flex-col items-center justify-center gap-5 rounded-[2.5rem] border border-red-500/20 bg-red-500/[0.05] px-6 py-12 text-center shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-md">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-red-500/20 bg-red-500/10 text-red-300">
                        <AlertCircle className="h-6 w-6" />
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-200">
                            Unable to load packages
                        </p>
                        <p className="max-w-md text-sm leading-6 text-red-100/80">
                            The package list could not be loaded right now. Please try again.
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => void onFetchPackages()}
                        className="rounded-full border-red-500/30 bg-transparent text-red-100 hover:bg-red-500/10 hover:text-red-50"
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Try Again
                    </Button>
                </div>
            </div>
        );
    }

    if (packages.length === 0) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="flex min-h-[320px] flex-col items-center justify-center gap-5 rounded-[2.5rem] border border-white/12 bg-white/[0.06] px-6 py-12 text-center shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-md">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary">
                        <AlertCircle className="h-6 w-6" />
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-foreground">
                            No packages available
                        </p>
                        <p className="max-w-md text-sm leading-6 text-muted-foreground/80">
                            There are no active packages to show at the moment. Please check back later.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto">
            <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10"
            >
                {packages.map((pkg) => (
                    <motion.button
                        key={pkg.id}
                        whileHover={{ y: -8 }}
                        onClick={() => onSelect(pkg)}
                        className={cn(
                            "group relative text-left flex flex-col p-6 md:p-8 rounded-[2.5rem] border transition-all duration-500",
                            "border-white/12 bg-white/[0.06] shadow-[0_20px_60px_rgba(0,0,0,0.24)] backdrop-blur-md",
                            selectedPackage?.id === pkg.id
                                ? "border-primary ring-2 ring-primary/25 shadow-[0_24px_70px_rgba(0,0,0,0.3)]"
                                : "hover:border-primary/45 hover:bg-white/[0.08] hover:shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
                        )}
                    >
                        <div className="mb-6 space-y-3">
                            <h3 className="text-2xl font-serif text-foreground group-hover:text-primary transition-colors duration-300">
                                {pkg.name}
                            </h3>
                            <p className="text-sm text-muted-foreground/80 line-clamp-3 font-light leading-relaxed">
                                {pkg.description}
                            </p>
                        </div>

                        <div className="mt-auto space-y-8">
                            <div className="flex items-baseline gap-3">
                                <span className="text-3xl font-semibold text-foreground tracking-tight">
                                    {formatMYR(pkg.finalPrice)}
                                </span>
                                {pkg.discount && pkg.discount > 0 ? (
                                    <span className="text-base text-muted-foreground/40 line-through">
                                        {formatMYR(pkg.price)}
                                    </span>
                                ) : null}
                            </div>

                            <div className="space-y-4 pt-6 border-t border-white/10">
                                <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/8 px-3 py-1.5 text-[10px] text-primary uppercase tracking-[0.2em] font-semibold">
                                    {pkg.durationMinutes} Minutes Session
                                </div>

                                {pkg.offers && pkg.offers.length > 0 && (
                                    <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground/75 marker:text-primary/60">
                                        {pkg.offers.slice(0, 4).map((offer, idx) => (
                                            <li key={idx} className="leading-relaxed">
                                                {offer}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </motion.button>
                ))}
            </motion.div>
        </div>
    );
};
